import { useState, useRef, useEffect } from 'react';

export default function Field({ label, type = "text", placeholder, value, onChange, options, required, note, rows = 3, error, tooltip, disabled, className = "", ...props }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const base = `w-full px-3 py-2.5 border rounded-lg text-sm transition-all placeholder:text-slate-300 dark:placeholder-slate-500 focus:outline-none focus:ring-2 ${
    disabled 
      ? "opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-border dark:border-slate-700" 
      : `text-ink bg-white dark:bg-slate-800 dark:text-white ${
          error 
            ? "!border-red-500 ring-1 !ring-red-500 focus:!ring-red-200 focus:!border-red-500" 
            : "border-border dark:border-slate-700 focus:ring-primary/20 focus:border-primary"
        }`
  } ${className}`;
  
  return (
    <div className="min-w-0">
      {label && (
        <label className="flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
          <span>{label}</span>
          {required && <span className="text-red-500">*</span>}
          {tooltip && (
            <span title={tooltip} className="cursor-help text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex items-center">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </span>
          )}
        </label>
      )}
      {type === "select"
        ? <select value={value} onChange={onChange} className={base} disabled={disabled} aria-invalid={error ? "true" : undefined} data-invalid={error ? "true" : undefined} {...props}>
            {options?.map((o, i) => <option key={i} className="bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100" value={typeof o === "object" ? o.value : o}>{typeof o === "object" ? o.label : o}</option>)}
          </select>
        : type === "combobox"
        ? (
          <div className="relative" ref={wrapperRef}>
            <input 
              type="text"
              className={base}
              placeholder={placeholder || "Search..."}
              value={open ? search : ((options || []).find(o => (typeof o === 'object' ? String(o.value) : String(o)) === String(value))?.label || value || "")}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => { setOpen(true); setSearch(""); }}
              disabled={disabled}
              aria-invalid={error ? "true" : undefined}
              data-invalid={error ? "true" : undefined}
              {...props}
            />
            {/* Dropdown Chevron */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
              <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
            
            {open && !disabled && (
              <div className="absolute top-full left-0 mt-1.5 w-full max-h-60 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50">
                {(options || []).filter(o => {
                  const labelStr = typeof o === 'object' ? String(o.label) : String(o);
                  return labelStr.toLowerCase().includes(search.toLowerCase());
                }).map((o, i) => {
                  const val = typeof o === 'object' ? o.value : o;
                  const lbl = typeof o === 'object' ? o.label : o;
                  const isSelected = String(val) === String(value);
                  
                  return (
                    <div 
                      key={i} 
                      className={`px-3 py-2.5 text-sm cursor-pointer transition-colors flex items-center justify-between ${isSelected ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'}`}
                      onClick={() => {
                        onChange({ target: { value: val } });
                        setSearch("");
                        setOpen(false);
                      }}
                    >
                      <span>{lbl}</span>
                      {isSelected && <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}
                    </div>
                  );
                })}
                {(options || []).filter(o => {
                  const labelStr = typeof o === 'object' ? String(o.label) : String(o);
                  return labelStr.toLowerCase().includes(search.toLowerCase());
                }).length === 0 && (
                  <div className="px-3 py-3 text-sm text-center text-slate-500">No results found</div>
                )}
              </div>
            )}
          </div>
        )
        : type === "textarea"
        ? <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} className={`${base} resize-y`} disabled={disabled} aria-invalid={error ? "true" : undefined} data-invalid={error ? "true" : undefined} {...props} />
        : <input type={type} value={value ?? ""} onChange={onChange} placeholder={placeholder} className={`${base} ${type === 'date' ? 'min-h-[42px] cursor-pointer' : ''}`} disabled={disabled} aria-invalid={error ? "true" : undefined} data-invalid={error ? "true" : undefined} {...props} />
      }
      {error && <p className="text-xs text-red-500 font-medium mt-1">{error}</p>}
      {note && !error && <p className="text-xs text-muted mt-1">{note}</p>}
    </div>
  );
}
